/**
 * Speed Role - Format Pillar
 *
 * Formata valores de velocidade para apresentação.
 *
 * @example
 * import { formatSpeed } from '@attrx/role-morphic/speed/format';
 *
 * formatSpeed('kilometer_per_hour', 120);                        // "120 km/h"
 * formatSpeed('kilometer_per_hour', 120, { verbose: true });     // "120 kilometers per hour"
 * formatSpeed('mile_per_hour', 60);                              // "60 mph"
 */

import { SPEED_UNITS, type SpeedUnit } from "./constants";

// =============================================================================
// TYPES
// =============================================================================

export interface SpeedFormatOptions {
  /** Casas decimais (default: 2) */
  decimals?: number;
  /** Usa nome completo ao invés de símbolo */
  verbose?: boolean;
  /** Locale para formatação de número */
  locale?: string;
  /** Notação numérica */
  notation?: "standard" | "scientific" | "compact";
}

// =============================================================================
// FORMAT
// =============================================================================

/**
 * Formata valor de velocidade para apresentação
 *
 * @param variant - Unidade do valor
 * @param value - Valor numérico
 * @param options - Opções de formatação
 * @returns String formatada
 *
 * @example
 * formatSpeed('kilometer_per_hour', 120);                        // "120 km/h"
 * formatSpeed('kilometer_per_hour', 120, { verbose: true });     // "120 kilometers per hour"
 * formatSpeed('kilometer_per_hour', 1, { verbose: true });       // "1 kilometer per hour"
 * formatSpeed('mile_per_hour', 1000, { locale: 'en-US' });       // "1,000 mph"
 */
export function formatSpeed(
  variant: SpeedUnit,
  value: number,
  options: SpeedFormatOptions = {}
): string {
  const { decimals = 2, verbose = false, locale, notation } = options;
  const unit = SPEED_UNITS[variant];

  if (!unit) {
    throw new Error(`Unknown speed variant: ${variant}`);
  }

  // Formata número
  let formattedValue: string;

  if (locale) {
    const formatOptions: Intl.NumberFormatOptions = {
      minimumFractionDigits: 0,
      maximumFractionDigits: decimals,
    };

    if (notation) {
      formatOptions.notation = notation;
    }

    formattedValue = value.toLocaleString(locale, formatOptions);
  } else {
    formattedValue = Number(value.toFixed(decimals)).toString();
  }

  // Escolhe símbolo ou nome
  const separator = unit.noSpace ? "" : " ";

  if (verbose) {
    const name = value === 1 ? unit.singular : unit.plural;
    return `${formattedValue} ${name || unit.symbol}`;
  }

  return `${formattedValue}${separator}${unit.symbol}`;
}

/**
 * Retorna apenas o símbolo da unidade
 *
 * @example
 * getSpeedSymbol('kilometer_per_hour');  // "km/h"
 * getSpeedSymbol('mile_per_hour');       // "mph"
 * getSpeedSymbol('knot');                // "kn"
 */
export function getSpeedSymbol(variant: SpeedUnit): string {
  const unit = SPEED_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown speed variant: ${variant}`);
  }
  return unit.symbol;
}

/**
 * Retorna o nome da unidade (singular ou plural)
 *
 * @example
 * getSpeedName('kilometer_per_hour', 1);   // "kilometer per hour"
 * getSpeedName('kilometer_per_hour', 2);   // "kilometers per hour"
 * getSpeedName('knot', 1);                 // "knot"
 */
export function getSpeedName(variant: SpeedUnit, count: number = 1): string {
  const unit = SPEED_UNITS[variant];
  if (!unit) {
    throw new Error(`Unknown speed variant: ${variant}`);
  }
  return count === 1
    ? (unit.singular || unit.symbol)
    : (unit.plural || unit.symbol);
}
